/**
 * Receiver latency hints: what the guest asked its RTCRtpReceiver to do, and what the
 * receiver reports back.
 *
 * `jitterBufferTarget` (ms) and Chromium's older `playoutDelayHint` (seconds) are hints to
 * the receiver's MINIMUM playout target. Setting them to 0 asks for the lowest latency the
 * engine will give; it does not drain a buffer that has already stood up. The read-back
 * exists so the host can see whether the hint is in force at all - until now the value was
 * written once, from deprecated code, and never observed again.
 *
 * Browser-free: the receiver is typed structurally so tests use a plain object.
 */

/** Upper bound the engines accept for jitterBufferTarget (ms). Mirrors libs/Peer RECEIVER_HINT_MAX_MS. */
export const RECEIVER_HINT_MAX_MS = 4_000;

/** The low-latency target the guest applies by default. */
export const RECEIVER_HINT_LOW_LATENCY_TARGET_MS = 0;

export type ReceiverHintReadback = {
  /** receiver.jitterBufferTarget as read back after apply; null when the property is absent. */
  jitterBufferTargetMs: number | null;
  /** receiver.playoutDelayHint (seconds) converted to ms; null when the property is absent. */
  playoutDelayHintMs: number | null;
  /** performance.now()-domain ms at which the hints were last (re)applied. */
  appliedAtMs: number;
};

function isNumberOrNull(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

export function isReceiverHintReadback(value: unknown): value is ReceiverHintReadback {
  if (value === null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    isNumberOrNull(r['jitterBufferTargetMs']) &&
    isNumberOrNull(r['playoutDelayHintMs']) &&
    typeof r['appliedAtMs'] === 'number' &&
    Number.isFinite(r['appliedAtMs'])
  );
}
